import { Injectable } from "@angular/core";
import { SupabaseService } from "./supabase.service";
import { AuthService } from "./auth.service";
import { Observable, from, throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";

export interface SubscriptionTier {
  id: string;
  name: string;
  price_monthly: number;
  features: string[];
  created_at: string;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  tier_id: string;
  status: "pending" | "active" | "cancelled" | "expired";
  approved_at?: string;
  expires_at?: string;
  approved_by?: string;
  created_at: string;
  updated_at: string;
  tier?: SubscriptionTier;
}

@Injectable({
  providedIn: "root",
})
export class SubscriptionService {
  constructor(
    private supabaseService: SupabaseService,
    private authService: AuthService
  ) {}

  /**
   * Get all available subscription tiers
   */
  getSubscriptionTiers(): Observable<SubscriptionTier[]> {
    return from(
      this.supabaseService
        .getClient()
        .from("subscription_tiers")
        .select("*")
        .order("price_monthly", { ascending: true })
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data || [];
      }),
      catchError((error) => {
        console.error("Error fetching subscription tiers:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get current user's subscription status
   */
  getCurrentUserSubscription(): Observable<UserSubscription | null> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return throwError(() => new Error("User not authenticated"));
    }

    return from(
      this.supabaseService
        .getClient()
        .from("user_subscriptions")
        .select(`
          *,
          tier:subscription_tiers(*)
        `)
        .eq("user_id", userId)
        .eq("status", "active")
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error && error.code !== "PGRST116") {
          // PGRST116 is "no rows returned", which is fine
          throw error;
        }
        return data || null;
      }),
      catchError((error) => {
        console.error("Error fetching user subscription:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if current user has access to talent pool
   */
  hasAccessToTalentPool(): Observable<boolean> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return from([false]);
    }

    return this.getCurrentUserSubscription().pipe(
      map((subscription) => {
        return subscription?.status === "active";
      }),
      catchError((error) => {
        console.error("Error checking talent pool access:", error);
        return from([false]);
      })
    );
  }

  /**
   * Create a new subscription for the current user
   */
  createSubscription(tierId: string): Observable<UserSubscription> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return throwError(() => new Error("User not authenticated"));
    }

    const subscriptionData = {
      user_id: userId,
      tier_id: tierId,
      status: "pending" as const,
      created_at: new Date().toISOString(),
    };

    return from(
      this.supabaseService
        .getClient()
        .from("user_subscriptions")
        .insert([subscriptionData])
        .select(`
          *,
          tier:subscription_tiers(*)
        `)
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data;
      }),
      catchError((error) => {
        console.error("Error creating subscription:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Cancel current user's subscription
   */
  cancelSubscription(): Observable<void> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      return throwError(() => new Error("User not authenticated"));
    }

    return from(
      this.supabaseService
        .getClient()
        .from("user_subscriptions")
        .update({ 
          status: "cancelled",
          updated_at: new Date().toISOString()
        })
        .eq("user_id", userId)
        .eq("status", "active")
    ).pipe(
      map(({ error }) => {
        if (error) throw error;
      }),
      catchError((error) => {
        console.error("Error cancelling subscription:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Admin function: Approve a pending subscription
   */
  approveSubscription(subscriptionId: string): Observable<UserSubscription> {
    const adminUserId = this.authService.getCurrentUser()?.id;
    if (!adminUserId) {
      return throwError(() => new Error("Admin not authenticated"));
    }

    const updates = {
      status: "active" as const,
      approved_at: new Date().toISOString(),
      approved_by: adminUserId,
      expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      updated_at: new Date().toISOString(),
    };

    return from(
      this.supabaseService
        .getClient()
        .from("user_subscriptions")
        .update(updates)
        .eq("id", subscriptionId)
        .select(`
          *,
          tier:subscription_tiers(*)
        `)
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data;
      }),
      catchError((error) => {
        console.error("Error approving subscription:", error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all pending subscriptions (admin function)
   */
  getPendingSubscriptions(): Observable<UserSubscription[]> {
    return from(
      this.supabaseService
        .getClient()
        .from("user_subscriptions")
        .select(`
          *,
          tier:subscription_tiers(*),
          user:users(first_name, last_name, email, job_title)
        `)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data || [];
      }),
      catchError((error) => {
        console.error("Error fetching pending subscriptions:", error);
        return throwError(() => error);
      })
    );
  }
} 